import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Tabs,
  Tab,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  People as PeopleIcon,
  SupervisorAccount as AdminIcon,
  PersonAdd as RepIcon,
  Feed as FeedIcon,
  TrendingUp as TrendingIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  Speed as SpeedIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { AuthContext } from '../Auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { decrypt } from '../../Security';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  const { isAuthenticated, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchDashboardData();
      fetchUsers();
    }
  }, [isAuthenticated, isAdmin]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Cookie-based auth only — no localStorage JWT needed
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_SERVER_URI}/adminWeb/dashboard-stats`, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const decryptedData = decrypt(data);
      setDashboardData(decryptedData.stats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // Cookie-based auth only — no localStorage JWT needed
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_SERVER_URI}/adminWeb/users?name=&email=`, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) return;

      const data = await response.json();
      const decryptedData = decrypt(data);
      setUsers(decryptedData.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleRoleUpdate = async () => {
    if (!selectedUser || !newRole) return;

    try {
      setUpdateLoading(true);
      // Cookie-based auth only — no localStorage JWT needed
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_SERVER_URI}/adminWeb/users/${selectedUser._id}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const decryptedError = errorData?.data ? decrypt(errorData) : {};
        throw new Error(decryptedError.msg || `HTTP ${response.status}`);
      }

      // Refresh data
      await fetchUsers();
      await fetchDashboardData();

      setRoleDialogOpen(false);
      setSelectedUser(null);
      setNewRole('');
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdateLoading(false);
    }
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setRoleDialogOpen(true);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'representative': return 'warning';
      case 'user': return 'primary';
      default: return 'default';
    }
  };

  const StatCard = ({ title, value, icon, color = 'primary', subtitle, trend, progressValue }) => (
    <Card sx={{ height: '100%', position: 'relative', borderRadius: '16px' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography color="textSecondary" gutterBottom variant="overline">
              {title}
            </Typography>
            <Typography variant="h4" component="h2">
              {value?.toLocaleString() || 0}
            </Typography>
            {subtitle && (
              <Typography color="textSecondary" variant="body2">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingIcon sx={{ fontSize: 16, mr: 0.5, color: trend > 0 ? 'success.main' : 'error.main' }} />
                <Typography variant="caption" color={trend > 0 ? 'success.main' : 'error.main'}>
                  {trend > 0 ? '+' : ''}{trend}%
                </Typography>
              </Box>
            )}
            {progressValue !== undefined && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={progressValue} 
                  sx={{ height: 6, borderRadius: 3 }}
                  color={color}
                />
                <Typography variant="caption" color="textSecondary">
                  {progressValue.toFixed(1)}%
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ color: `${color}.main`, ml: 2 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const AnalyticsCard = ({ title, children, icon }) => (
    <Card sx={{ height: '100%', borderRadius: '16px' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon && <Box sx={{ mr: 1, color: 'primary.main' }}>{icon}</Box>}
          <Typography variant="h6">{title}</Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  const MetricRow = ({ label, value, subValue, color = 'text.primary' }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
      <Typography variant="body2">{label}</Typography>
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="body2" sx={{ color, fontWeight: 'medium' }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        {subValue && (
          <Typography variant="caption" color="textSecondary">
            {subValue}
          </Typography>
        )}
      </Box>
    </Box>
  );

  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => { setError(null); fetchDashboardData(); fetchUsers(); }}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // Enhanced chart data
  const userGrowthChartData = {
    labels: dashboardData?.growthTrends?.userGrowth?.map(item => 
      `${item._id.month}/${item._id.year}`) || [],
    datasets: [
      {
        label: 'New Users',
        data: dashboardData?.growthTrends?.userGrowth?.map(item => item.count) || [],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1
      }
    ]
  };

  const contentGrowthChartData = {
    labels: dashboardData?.growthTrends?.contentGrowth?.map(item => 
      `${item._id.month}/${item._id.year}`) || [],
    datasets: [
      {
        label: 'New Posts',
        data: dashboardData?.growthTrends?.contentGrowth?.map(item => item.count) || [],
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.1
      }
    ]
  };

  const userRoleDistributionData = {
    labels: ['Regular Users', 'Representatives', 'Administrators'],
    datasets: [
      {
        data: [
          dashboardData?.userAnalytics?.basic?.users || 0,
          dashboardData?.userAnalytics?.basic?.representatives || 0,
          dashboardData?.userAnalytics?.basic?.admins || 0
        ],
        backgroundColor: [
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(255, 99, 132, 0.8)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        borderWidth: 1
      }
    ]
  };

  const geographicChartData = {
    labels: dashboardData?.userAnalytics?.geographic?.slice(0, 10).map(item => item.state) || [],
    datasets: [
      {
        label: 'Users by State',
        data: dashboardData?.userAnalytics?.geographic?.slice(0, 10).map(item => item.count) || [],
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: { display: false }
    },
    scales: {
      y: { beginAtZero: true }
    }
  };

  return (
    <Box>
      {/* Header */}
      <AppBar position="static" sx={{ mb: 3 }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/main')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Enterprise Admin Dashboard
          </Typography>
          <Tooltip title="Refresh Data">
            <IconButton color="inherit" onClick={fetchDashboardData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Key Metrics Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="Total Users"
              value={dashboardData?.overview?.totalUsers}
              icon={<PeopleIcon sx={{ fontSize: 40 }} />}
              color="primary"
              subtitle={`${dashboardData?.userAnalytics?.basic?.verified || 0} verified`}
              trend={dashboardData?.userAnalytics?.growth?.growthRate}
              progressValue={dashboardData?.userAnalytics?.engagement?.retentionRate}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="Representatives"
              value={dashboardData?.overview?.totalReps}
              icon={<RepIcon sx={{ fontSize: 40 }} />}
              color="warning"
              subtitle={`${dashboardData?.representativeAnalytics?.basic?.withUserAccount || 0} with accounts`}
              trend={dashboardData?.representativeAnalytics?.engagement?.engagementRate}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="Active Users"
              value={dashboardData?.userAnalytics?.engagement?.weeklyActive}
              icon={<TrendingIcon sx={{ fontSize: 40 }} />}
              color="success"
              subtitle={`${dashboardData?.userAnalytics?.engagement?.dailyActive || 0} daily active`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="Content Posts"
              value={dashboardData?.contentAnalytics?.posts?.total}
              icon={<FeedIcon sx={{ fontSize: 40 }} />}
              color="info"
              subtitle={`${dashboardData?.contentAnalytics?.posts?.thisWeek || 0} this week`}
              trend={dashboardData?.contentAnalytics?.posts?.growthRate}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="System Health"
              value="99.9%"
              icon={<CheckCircleIcon sx={{ fontSize: 40 }} />}
              color="success"
              subtitle="Uptime"
            />
          </Grid>
        </Grid>

        {/* Tabbed Analytics */}
        <Card sx={{ mb: 4, borderRadius: '16px' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
              <Tab label="Overview" icon={<AnalyticsIcon />} />
              <Tab label="User Analytics" icon={<PeopleIcon />} />
              <Tab label="Representative Analytics" icon={<RepIcon />} />
              <Tab label="Content Analytics" icon={<FeedIcon />} />
              <Tab label="Geographic Distribution" icon={<LanguageIcon />} />
              <Tab label="System Health" icon={<SecurityIcon />} />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <CardContent>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                {/* User Growth Chart */}
                <Grid item xs={12} md={8}>
                  <AnalyticsCard title="User Growth Trend" icon={<TrendingIcon />}>
                    <Line data={userGrowthChartData} options={chartOptions} height={100} />
                  </AnalyticsCard>
                </Grid>
                
                {/* User Role Distribution */}
                <Grid item xs={12} md={4}>
                  <AnalyticsCard title="User Distribution" icon={<PeopleIcon />}>
                    <Doughnut data={userRoleDistributionData} options={{ responsive: true }} />
                  </AnalyticsCard>
                </Grid>

                {/* Quick Stats */}
                <Grid item xs={12} md={6}>
                  <AnalyticsCard title="Recent Activity" icon={<StarIcon />}>
                    <MetricRow 
                      label="New Users Today" 
                      value={dashboardData?.userAnalytics?.growth?.newToday || 0}
                      color="success.main"
                    />
                    <MetricRow 
                      label="New Users This Week" 
                      value={dashboardData?.userAnalytics?.growth?.newThisWeek || 0}
                      color="info.main"
                    />
                    <MetricRow 
                      label="New Posts Today" 
                      value={dashboardData?.contentAnalytics?.posts?.today || 0}
                      color="warning.main"
                    />
                    <MetricRow 
                      label="New Posts This Week" 
                      value={dashboardData?.contentAnalytics?.posts?.thisWeek || 0}
                      color="primary.main"
                    />
                  </AnalyticsCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AnalyticsCard title="Top Performing States" icon={<LanguageIcon />}>
                    {dashboardData?.representativeAnalytics?.topStates?.slice(0, 5).map((state, index) => (
                      <MetricRow 
                        key={index}
                        label={state.state || 'Unknown'} 
                        value={state.count}
                        subValue={`#${index + 1}`}
                      />
                    ))}
                  </AnalyticsCard>
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <AnalyticsCard title="User Engagement Metrics" icon={<PeopleIcon />}>
                    <MetricRow label="Total Users" value={dashboardData?.userAnalytics?.basic?.total} />
                    <MetricRow label="Daily Active" value={dashboardData?.userAnalytics?.engagement?.dailyActive} />
                    <MetricRow label="Weekly Active" value={dashboardData?.userAnalytics?.engagement?.weeklyActive} />
                    <MetricRow label="Monthly Active" value={dashboardData?.userAnalytics?.engagement?.monthlyActive} />
                    <MetricRow 
                      label="Retention Rate" 
                      value={`${dashboardData?.userAnalytics?.engagement?.retentionRate || 0}%`}
                      color="success.main"
                    />
                  </AnalyticsCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AnalyticsCard title="User Categories" icon={<AdminIcon />}>
                    <MetricRow label="Regular Users" value={dashboardData?.userAnalytics?.basic?.users} />
                    <MetricRow label="Representatives" value={dashboardData?.userAnalytics?.basic?.representatives} />
                    <MetricRow label="Administrators" value={dashboardData?.userAnalytics?.basic?.admins} />
                    <MetricRow label="Verified Users" value={dashboardData?.userAnalytics?.basic?.verified} />
                    <MetricRow 
                      label="Blocked Users" 
                      value={dashboardData?.userAnalytics?.basic?.blocked}
                      color="error.main"
                    />
                  </AnalyticsCard>
                </Grid>

                <Grid item xs={12}>
                  <AnalyticsCard title="User Geographic Distribution" icon={<LanguageIcon />}>
                    <Bar data={geographicChartData} options={chartOptions} height={100} />
                  </AnalyticsCard>
                </Grid>
              </Grid>
            )}

            {currentTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <AnalyticsCard title="Representative Overview" icon={<RepIcon />}>
                    <MetricRow label="Total Representatives" value={dashboardData?.representativeAnalytics?.basic?.total} />
                    <MetricRow label="With User Accounts" value={dashboardData?.representativeAnalytics?.basic?.withUserAccount} />
                    <MetricRow label="Verified" value={dashboardData?.representativeAnalytics?.basic?.verified} />
                    <MetricRow label="With Profile Pictures" value={dashboardData?.representativeAnalytics?.basic?.withProfilePicture} />
                    <MetricRow 
                      label="Avg Posts per Rep" 
                      value={dashboardData?.representativeAnalytics?.engagement?.avgPostsPerRep}
                      color="info.main"
                    />
                  </AnalyticsCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AnalyticsCard title="Most Followed Representatives" icon={<StarIcon />}>
                    {dashboardData?.representativeAnalytics?.engagement?.mostFollowed?.slice(0, 5).map((rep, index) => (
                      <MetricRow 
                        key={index}
                        label={rep.name || 'Unknown'} 
                        value={rep.followerCount || 0}
                        subValue={rep.current_elected_position}
                      />
                    ))}
                  </AnalyticsCard>
                </Grid>

                <Grid item xs={12}>
                  <AnalyticsCard title="Representatives by State" icon={<LanguageIcon />}>
                    <Grid container spacing={2}>
                      {dashboardData?.representativeAnalytics?.geographic?.slice(0, 12).map((state, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <MetricRow 
                            label={state.state || 'Unknown'} 
                            value={state.count}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </AnalyticsCard>
                </Grid>
              </Grid>
            )}

            {currentTab === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <AnalyticsCard title="Content Growth Over Time" icon={<FeedIcon />}>
                    <Line data={contentGrowthChartData} options={chartOptions} height={100} />
                  </AnalyticsCard>
                </Grid>

                <Grid item xs={12} md={4}>
                  <AnalyticsCard title="Content Statistics" icon={<AnalyticsIcon />}>
                    <MetricRow label="Total Posts" value={dashboardData?.contentAnalytics?.posts?.total} />
                    <MetricRow label="Posts Today" value={dashboardData?.contentAnalytics?.posts?.today} />
                    <MetricRow label="Posts This Week" value={dashboardData?.contentAnalytics?.posts?.thisWeek} />
                    <MetricRow label="Posts This Month" value={dashboardData?.contentAnalytics?.posts?.thisMonth} />
                    <MetricRow 
                      label="Growth Rate" 
                      value={`${dashboardData?.contentAnalytics?.posts?.growthRate || 0}%`}
                      color="success.main"
                    />
                  </AnalyticsCard>
                </Grid>
              </Grid>
            )}

            {currentTab === 4 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <AnalyticsCard title="Top States by Users" icon={<PeopleIcon />}>
                    {dashboardData?.userAnalytics?.geographic?.slice(0, 10).map((state, index) => (
                      <MetricRow 
                        key={index}
                        label={state.state || 'Unknown'} 
                        value={state.count}
                        subValue={`Rank #${index + 1}`}
                      />
                    ))}
                  </AnalyticsCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AnalyticsCard title="Top States by Representatives" icon={<RepIcon />}>
                    {dashboardData?.representativeAnalytics?.topStates?.slice(0, 10).map((state, index) => (
                      <MetricRow 
                        key={index}
                        label={state.state || 'Unknown'} 
                        value={state.count}
                        subValue={`Rank #${index + 1}`}
                      />
                    ))}
                  </AnalyticsCard>
                </Grid>

                <Grid item xs={12}>
                  <AnalyticsCard title="User Distribution by State" icon={<LanguageIcon />}>
                    <Bar data={geographicChartData} options={chartOptions} height={100} />
                  </AnalyticsCard>
                </Grid>
              </Grid>
            )}

            {currentTab === 5 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <AnalyticsCard title="System Health Metrics" icon={<SpeedIcon />}>
                    <MetricRow label="System Status" value="Healthy" color="success.main" />
                    <MetricRow label="Uptime" value="99.9%" color="success.main" />
                    <MetricRow label="Response Time" value="< 200ms" color="info.main" />
                    <MetricRow label="Active Users" value={dashboardData?.systemHealth?.metrics?.activeUsers} />
                    <MetricRow 
                      label="Blocked Users" 
                      value={dashboardData?.systemHealth?.metrics?.blockedUsers}
                      color="error.main"
                    />
                  </AnalyticsCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AnalyticsCard title="Security Overview" icon={<SecurityIcon />}>
                    <MetricRow label="Total Users" value={dashboardData?.security?.verifiedUsers + dashboardData?.security?.blockedUsers || 0} />
                    <MetricRow label="Verified Users" value={dashboardData?.security?.verifiedUsers} color="success.main" />
                    <MetricRow label="Blocked Users" value={dashboardData?.security?.blockedUsers} color="error.main" />
                    <MetricRow label="Security Incidents" value={dashboardData?.security?.securityIncidents || 0} />
                    <MetricRow label="Failed Logins" value={dashboardData?.security?.failedLogins || 0} />
                  </AnalyticsCard>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* User Management Section */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              User Management
            </Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.slice(0, 20).map((user) => (
                    <TableRow key={user._id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          color={getRoleColor(user.role)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.blocked ? 'Blocked' : 'Active'}
                          color={user.blocked ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => openRoleDialog(user)}
                          sx={{ mr: 1 }}
                        >
                          Change Role
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Role Update Dialog */}
      <Dialog 
        open={roleDialogOpen} 
        onClose={() => setRoleDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px', minWidth: 400 } }}
      >
        <DialogTitle>Update User Role</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Update role for: {selectedUser?.name} ({selectedUser?.email})
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              label="Role"
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="representative">Representative</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleRoleUpdate} 
            disabled={updateLoading}
            variant="contained"
          >
            {updateLoading ? <CircularProgress size={20} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
